export interface MappedApiError {
  userMessage: string;
  requiresReauth: boolean;
  isRateLimited: boolean;
  traceId?: string;
  httpStatus?: number;
  errorCode?: string;
}

const ERROR_CODE_MESSAGES: Record<string, string> = {
  STALE_ORDER_VERSION: 'The order was updated elsewhere. Please refresh and try again.',
  INSUFFICIENT_STOCK: 'One or more items in your cart are currently out of stock.',
  INVALID_CHECKOUT_PAYLOAD: 'Invalid order checkout parameters. Please review your cart.',
  PAYMENT_UNAUTHORIZED: 'Payment authorization failed. Please check your payment method.',
  GUEST_CHECKOUT_SIGN_IN_REQUIRED: 'An account already exists for this email. Sign in to checkout.',
  IC_AGREEMENT_REQUIRED: 'Please sign the updated independent contractor agreement to continue.',
  IC_AGREEMENT_VERSION_STALE: 'The independent contractor agreement was updated. Sign the new version.',
  DEVICE_DRIFT: 'Your session is no longer valid on this device. Please sign in again.',
  RISK_DENIED: 'This action is temporarily unavailable. Please contact support if this continues.',
  RISK_MANUAL_REVIEW_REQUIRED: 'Your request is under security review. We will notify you shortly.',
  RISK_STEP_UP_REQUIRED: 'Please confirm your identity and try again.',
  STEP_UP_REQUIRED: 'Please confirm your identity and try again.',
  AUTH_FIREBASE_DISABLED: 'This sign-in method is not available yet. Please use email and password.'
};

export interface ApiErrorLike {
  message?: string;
  status?: number;
  statusCode?: number;
  code?: string;
  errorCode?: string;
  traceId?: string;
  response?: {
    status?: number;
    headers?: Record<string, string | undefined>;
    data?: {
      code?: string;
      message?: string;
      traceId?: string;
    };
  };
  data?: {
    code?: string;
    message?: string;
    traceId?: string;
  };
}

export type AppErrorPayload =
  | Error
  | ApiErrorLike
  | string
  | null
  | undefined;

export function mapApiError(error: AppErrorPayload, fallback = 'Something went wrong. Please try again.'): MappedApiError {
  if (!error) {
    return { userMessage: fallback, requiresReauth: false, isRateLimited: false };
  }

  if (typeof error === 'string') {
    return { userMessage: error.trim() || fallback, requiresReauth: false, isRateLimited: false };
  }

  const errObj = error as ApiErrorLike;
  const resObj = errObj.response;
  const resData = resObj?.data;
  const errData = errObj.data;
  const headers = resObj?.headers;

  const httpStatus = typeof errObj.status === 'number'
    ? errObj.status
    : typeof errObj.statusCode === 'number'
    ? errObj.statusCode
    : typeof resObj?.status === 'number'
    ? resObj.status
    : undefined;

  const traceId = (typeof errObj.traceId === 'string' ? errObj.traceId : undefined) ||
    (typeof headers?.['x-trace-id'] === 'string' ? headers['x-trace-id'] : undefined) ||
    (typeof resData?.traceId === 'string' ? resData.traceId : undefined) ||
    (typeof errData?.traceId === 'string' ? errData.traceId : undefined);

  const rawCode = errObj.errorCode || errObj.code || resData?.code || '';
  const errorCode = typeof rawCode === 'string' ? rawCode.toUpperCase().trim() : '';

  const rawMsg = errObj.message || resData?.message;
  const apiMessage = typeof rawMsg === 'string' ? rawMsg : undefined;

  let requiresReauth = httpStatus === 401;
  let isRateLimited = httpStatus === 429;

  let userMessage = fallback;

  if (errorCode && ERROR_CODE_MESSAGES[errorCode]) {
    userMessage = ERROR_CODE_MESSAGES[errorCode];
  } else if (apiMessage && typeof apiMessage === 'string' && apiMessage.trim().length > 0) {
    userMessage = apiMessage.trim();
  } else if (httpStatus === 401) {
    userMessage = 'Session expired. Sign in again to continue.';
  } else if (httpStatus === 403) {
    userMessage = 'This action is not permitted for your role or policy.';
  } else if (httpStatus === 429) {
    userMessage = 'Too many requests. Please wait a moment before trying again.';
  } else if (typeof httpStatus === 'number' && httpStatus >= 500) {
    userMessage = 'Server is currently experiencing high load. Please try again shortly.';
  }

  if (traceId) {
    userMessage = `${userMessage} (Ref: ${traceId.slice(0, 8)})`;
  }

  return {
    userMessage,
    requiresReauth,
    isRateLimited,
    traceId,
    httpStatus,
    errorCode: errorCode || undefined
  };
}
